import axios from 'axios';
import { Resource } from '@domain/base/Resource';
import { AmazonRegion, IAmazonConnectorRepository } from '@domain/connection/IMarketplaceRepository';
import { Connection } from '@domain/connection/models/Connection';
import { AmazonProductMatchMapping, AmazonSettings } from '@domain/connection/models/ConnectionSettings';
import { AmazonMatchPreviewResponse, CompleteAmazonSetupResponse } from '@domain/connection/models/AmazonMatchPreview';
import { FullSyncJob } from '@domain/connection/models/ImportJob';
import { ConnectionMapper } from '../mappers/MarketplaceMapper';
import { AmazonConnectorDataSource } from '../datasources/AmazonConnectorDataSource';

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    const message = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
    if (message) return message;
  }
  return fallback;
}

export class AmazonConnectorRepository implements IAmazonConnectorRepository {
  constructor(private dataSource: AmazonConnectorDataSource) { }

  async startAuthorization(region: AmazonRegion, connectionId?: string): Promise<Resource<{ authUrl: string; state: string; redirectUri: string }>> {
    try {
      const data = await this.dataSource.startAuthorization(region, connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to start Amazon sign-in') } };
    }
  }

  async connectSandbox(region: AmazonRegion, connectionId?: string): Promise<Resource<Connection>> {
    try {
      const data = await this.dataSource.connectSandbox(region, connectionId);
      return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to connect the Amazon sandbox') } };
    }
  }

  async completeAuthorization(code: string, state: string, sellingPartnerId?: string): Promise<Resource<Connection>> {
    try {
      const data = await this.dataSource.completeAuthorization(code, state, sellingPartnerId);
      return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to connect to Amazon') } };
    }
  }

  async syncProducts(connectionId: string): Promise<Resource<{ jobId: string }>> {
    try {
      const data = await this.dataSource.syncProducts(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to import Amazon products') } };
    }
  }

  async syncInventory(connectionId: string): Promise<Resource<{ jobId: string }>> {
    try {
      const data = await this.dataSource.syncInventory(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to sync Amazon inventory') } };
    }
  }

  async syncAllOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {
    try {
      const data = await this.dataSource.syncAllOrders(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to sync Amazon orders') } };
    }
  }

  async syncNewOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {
    try {
      const data = await this.dataSource.syncNewOrders(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to sync Amazon orders') } };
    }
  }

  async getSettings(connectionId: string): Promise<Resource<AmazonSettings>> {
    try {
      const data = await this.dataSource.getSettings(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to load Amazon settings') } };
    }
  }

  async updateSettings(connectionId: string, settings: Partial<AmazonSettings>): Promise<Resource<AmazonSettings>> {
    try {
      const data = await this.dataSource.updateSettings(connectionId, settings);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to save Amazon settings') } };
    }
  }

  async getMatchPreview(
    connectionId: string,
    mapping?: Partial<AmazonProductMatchMapping>,
  ): Promise<Resource<AmazonMatchPreviewResponse>> {
    try {
      const data = await this.dataSource.getMatchPreview(connectionId, mapping);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to load match preview') } };
    }
  }

  async completeSetup(
    connectionId: string,
    productMatchMapping: AmazonProductMatchMapping,
  ): Promise<Resource<CompleteAmazonSetupResponse>> {
    try {
      const data = await this.dataSource.completeSetup(connectionId, productMatchMapping);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to complete setup') } };
    }
  }

  async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
    try {
      const data = await this.dataSource.syncFull(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to start full sync') } };
    }
  }
}
